'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { Card, Skeleton } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { ArrowRight } from '@repo/ui/icons';
import React, { useMemo } from 'react';
import { type Warning, WarningIcon } from '@/components/common/WarningIcon';
import { getWarningLevelColorClass } from '@/helpers/warnings';
const Root = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof Card>>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("grow w-full gap-md flex-col px-md", cn("bg-muted shadow-none", className));
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <Card className={t1} ref={ref} {...props}>{children}</Card>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
Root.displayName = 'TransactionInfoCard.Root';
const Header = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("gap-xs flex flex-row items-center", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <div className={t1} ref={ref} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
Header.displayName = 'TransactionInfoCard.Header';
const Content = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("gap-md flex flex-col", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <div className={t1} ref={ref} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
Content.displayName = 'TransactionInfoCard.Content';
const List = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("gap-xs flex flex-col", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <div className={t1} ref={ref} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
List.displayName = 'TransactionInfoCard.List';
export interface ValueProps<T extends BigIntish> {
  value: Fetchable<T>;
  nullValue?: Fetchable<T>;
  /** Extra text-* classes merged onto the default `text-3xs`. */
  textClassName?: string;
}
const Value = t0 => {
  const $ = _c(18);
  const {
    value,
    nullValue,
    textClassName
  } = t0;
  if (value === undefined) {
    let t1;
    if ($[0] !== textClassName) {
      t1 = cn("text-3xs h-[1lh] w-5 rounded-[20px]", textClassName);
      $[0] = textClassName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Skeleton className={t1} />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (value === null) {
    let t1;
    if ($[4] !== textClassName) {
      t1 = cn("text-3xs", textClassName);
      $[4] = textClassName;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== nullValue) {
      t2 = nullValue?.toString();
      $[6] = nullValue;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t1 || $[9] !== t2) {
      t3 = <span className={t1}>{t2}</span>;
      $[8] = t1;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  let t1;
  if ($[11] !== textClassName) {
    t1 = cn("text-3xs", textClassName);
    $[11] = textClassName;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== value) {
    t2 = value.toString();
    $[13] = value;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== t1 || $[16] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
Value.displayName = 'TransactionInfoCard.Value';
export interface ValueChangeProps<T extends BigIntish> {
  initialProps: ValueProps<T>;
  finalProps: ValueProps<T>;
  nullValue?: T;
  formatter(value: T): Fetchable<string>;
}
const ValueChange = t0 => {
  const $ = _c(20);
  const {
    initialProps,
    finalProps,
    nullValue: t1,
    formatter
  } = t0;
  const nullValue = t1 === undefined ? ZERO as T : t1;
  let t2;
  if ($[0] !== formatter || $[1] !== initialProps.value) {
    t2 = isDefined(initialProps.value) ? formatter(initialProps.value) : initialProps.value;
    $[0] = formatter;
    $[1] = initialProps.value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formattedInitial = t2;
  let t3;
  if ($[3] !== finalProps.value || $[4] !== formatter) {
    t3 = isDefined(finalProps.value) ? formatter(finalProps.value) : finalProps.value;
    $[3] = finalProps.value;
    $[4] = formatter;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedFinal = t3;
  let t4;
  if ($[6] !== formatter || $[7] !== nullValue) {
    t4 = formatter(nullValue);
    $[6] = formatter;
    $[7] = nullValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formattedNullValue = t4;
  const t5 = initialProps.textClassName ?? "text-foreground";
  let t6;
  if ($[9] !== formattedInitial || $[10] !== formattedNullValue || $[11] !== t5) {
    t6 = <Value value={formattedInitial} textClassName={t5} nullValue={formattedNullValue} />;
    $[9] = formattedInitial;
    $[10] = formattedNullValue;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== finalProps.textClassName || $[14] !== formattedFinal || $[15] !== formattedNullValue) {
    t7 = formattedFinal && <div className="animate-bounce-from-left flex flex-row items-center gap-[2px]"><Icon className="text-muted-foreground size-[16px]" icon={<ArrowRight />} /><Value value={formattedFinal} textClassName={finalProps.textClassName} nullValue={formattedNullValue} /></div>;
    $[13] = finalProps.textClassName;
    $[14] = formattedFinal;
    $[15] = formattedNullValue;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="inline-flex flex-wrap items-center justify-end gap-[2px]">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
ValueChange.displayName = 'TransactionInfoCard.ValueChange';
const ListItem = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & {
  label: React.ReactNode;
  value: React.ReactNode;
  warning?: Warning | null;
}>((t0, ref) => {
  const $ = _c(27);
  let className;
  let label;
  let props;
  let value;
  let warning;
  if ($[0] !== t0) {
    ({
      label,
      value,
      warning,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = label;
    $[3] = props;
    $[4] = value;
    $[5] = warning;
  } else {
    className = $[1];
    label = $[2];
    props = $[3];
    value = $[4];
    warning = $[5];
  }
  let t1;
  bb0: {
    if (!warning) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[6] !== warning.level) {
      t2 = getWarningLevelColorClass(warning.level);
      $[6] = warning.level;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = t2;
  }
  const warningColorClass = t1;
  let t2;
  if ($[8] !== className || $[9] !== warningColorClass) {
    t2 = cn("flex flex-row items-center w-full justify-between", cn(warningColorClass, className));
    $[8] = className;
    $[9] = warningColorClass;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = warningColorClass || "text-secondary-foreground";
  let t4;
  if ($[11] !== t3) {
    t4 = cn("text-3xs", t3);
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== label || $[14] !== t4) {
    t5 = <span className={t4}>{label}</span>;
    $[13] = label;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== warning) {
    t6 = warning && <WarningIcon warnings={[warning]} isSimple={true} />;
    $[16] = warning;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <div className="gap-xs flex flex-row items-center">{t5}{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== props || $[22] !== ref || $[23] !== t2 || $[24] !== t7 || $[25] !== value) {
    t8 = <div className={t2} ref={ref} {...props}>{t7}{value}</div>;
    $[21] = props;
    $[22] = ref;
    $[23] = t2;
    $[24] = t7;
    $[25] = value;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
});
ListItem.displayName = 'TransactionInfoCard.ListItem';
export const TransactionInfoCard = {
  Root,
  Header,
  List,
  ListItem,
  Value,
  ValueChange,
  Content
};